import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Chip, TableLoadingSkeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash, Plus, PencilSimple, FileXls, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import ResidentsMasterImport from "@/components/ResidentsMasterImport";

const ROLES = ["admin", "committee", "resident"];
const isPlaceholderEmail = (email) => (email || "").endsWith("@no-email.maintyn.local");

export default function Users() {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [flats, setFlats] = useState([]);
    const [open, setOpen] = useState(false);
    const [masterImportOpen, setMasterImportOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "resident", flat_id: "" });
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const [u, f] = await Promise.all([api.get("/users"), api.get("/flats")]);
            setUsers(u.data);
            setFlats(f.data);
            return u.data;
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    function openCreate() {
        setEditing(null);
        setForm({ name: "", email: "", phone: "", password: "", role: "resident", flat_id: "" });
        setOpen(true);
    }
    function openEdit(u) {
        setEditing(u);
        setForm({ name: u.name, email: isPlaceholderEmail(u.email) ? "" : u.email, phone: u.phone || "", password: "", role: u.role, flat_id: u.flat_id || "" });
        setOpen(true);
    }

    // Flat 360 Profile deep-link: navigate here with state={{ openResidentId: "<user id>" }}
    // to open this resident's edit view directly — this IS the resident profile/detail view
    // for now (no separate route exists yet), per the existing Residents architecture.
    useEffect(() => {
        if (!location.state?.openResidentId) return;
        (async () => {
            const list = await load();
            const target = (list || []).find((u) => u.id === location.state.openResidentId);
            if (target) openEdit(target);
            else toast.error("Resident not found");
            navigate(location.pathname, { replace: true, state: {} });
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function submit(e) {
        e.preventDefault();
        try {
            if (editing) {
                const payload = { name: form.name, phone: form.phone, role: form.role, flat_id: form.flat_id || null };
                if (form.password) payload.password = form.password;
                await api.patch(`/users/${editing.id}`, payload);
                toast.success("Resident updated");
            } else {
                await api.post("/users", { ...form, flat_id: form.flat_id || null });
                toast.success("Resident added");
            }
            setOpen(false);
            load();
        } catch (e) { toast.error(formatError(e)); }
    }
    async function remove(u) {
        if (!confirm(`Delete ${u.name}?`)) return;
        try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatError(e)); }
    }

    const canManage = user?.role === "admin";

    const filteredUsers = users.filter(u => {
        if (!searchQuery) return true;
        
        const query = searchQuery.toLowerCase();
        const name = (u.name || "").toLowerCase();
        const email = (u.email || "").toLowerCase();
        const flatLabel = u.flat ? `${u.flat.block}-${u.flat.number}`.toLowerCase() : "";
        const phone = (u.phone || "").toLowerCase();
        
        return name.includes(query) || 
               email.includes(query) || 
               flatLabel.includes(query) ||
               phone.includes(query);
    });

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Community"
                title="Residents"
                description="Manage committee members, residents, and their flat assignments."
                actions={canManage && (
                    <div className="flex flex-wrap gap-2">
                        <Button data-testid="residents-master-onboarding-btn" onClick={() => setMasterImportOpen(true)} variant="outline"
                            className="rounded-full border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white">
                            <FileXls size={16} className="mr-1.5" /> Master Onboarding
                        </Button>
                        <Button data-testid="add-user-btn" onClick={openCreate} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white active:scale-[0.98]">
                            <Plus size={16} className="mr-1.5" /> Add resident
                        </Button>
                    </div>
                )}
            />

            {/* Search bar */}
            {users.length > 0 && (
                <div className="mb-4 relative">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
                    <Input 
                        type="text"
                        placeholder="Search by name, email, phone, or flat..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="max-w-md rounded-sm border-brand-line pl-10"
                        data-testid="user-search-input"
                    />
                </div>
            )}

            {loading ? (
                <TableLoadingSkeleton rows={5} cols={6} />
            ) : users.length === 0 ? (
                <EmptyState title="No residents yet" description="Add your first resident to get started." />
            ) : filteredUsers.length === 0 ? (
                <EmptyState title="No matches" description="Try a different search term." />
            ) : (
                <>
                    {/* Mobile scroll hint */}
                    <div className="md:hidden bg-brand-sage/30 border border-brand-action/20 rounded-sm px-3 py-2 mb-3 flex items-center gap-2 text-xs text-brand-ink">
                        <span className="flex-shrink-0">💡</span>
                        <span>Scroll table horizontally to see all columns and actions →</span>
                    </div>
                    <div className="bg-white border border-brand-line rounded-sm overflow-x-auto">
                    <table className="w-full text-sm min-w-[700px]">
                        <thead>
                            <tr className="bg-brand-bg border-b border-brand-line text-left">
                                <th className="py-3 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline whitespace-nowrap">Name</th>
                                <th className="py-3 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline whitespace-nowrap">Email</th>
                                <th className="py-3 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline whitespace-nowrap">Role</th>
                                <th className="py-3 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline whitespace-nowrap">Flat</th>
                                <th className="py-3 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline whitespace-nowrap">Relationship</th>
                                <th className="py-3 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline whitespace-nowrap">Phone</th>
                                {canManage && <th className="py-3 px-4 whitespace-nowrap"></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((u) => (
                                <tr key={u.id} className="border-b border-brand-line/60 hover:bg-brand-bg/50">
                                    <td className="py-3 px-4 font-medium text-brand-ink whitespace-nowrap">{u.name}</td>
                                    <td className="py-3 px-4 text-brand-inkSoft">{isPlaceholderEmail(u.email) ? "—" : u.email}</td>
                                    <td className="py-3 px-4 whitespace-nowrap"><Chip variant={u.role === "admin" ? "danger" : u.role === "committee" ? "warn" : "default"}>{u.role}</Chip></td>
                                    <td className="py-3 px-4 text-brand-inkSoft whitespace-nowrap">{u.flat ? `${u.flat.block}-${u.flat.number}` : "—"}</td>
                                    <td className="py-3 px-4 whitespace-nowrap" data-testid={`user-relationship-${u.id}`}>
                                        {u.relationship_label ? <Chip variant="neutral">{u.relationship_label}</Chip> : <span className="text-brand-inkSoft">—</span>}
                                    </td>
                                    <td className="py-3 px-4 text-brand-inkSoft whitespace-nowrap">{u.phone || "—"}</td>
                                    {canManage && (
                                        <td className="py-3 px-4 text-right whitespace-nowrap">
                                            <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="p-1.5 hover:bg-brand-sage rounded-sm mr-1">
                                                <PencilSimple size={14} />
                                            </button>
                                            <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)} className="p-1.5 hover:bg-[#FEF2F2] rounded-sm text-brand-action">
                                                <Trash size={14} />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                </>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-sm border-brand-line" data-testid="user-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl tracking-tight">{editing ? "Edit resident" : "Add resident"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Full name</Label>
                            <Input data-testid="user-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input data-testid="user-email-input" type="email" required disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm border-brand-line" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Phone</Label>
                                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-2">
                                <Label>Role</Label>
                                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                    <SelectTrigger data-testid="user-role-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Flat</Label>
                            <Select value={form.flat_id || "__none"} onValueChange={(v) => setForm({ ...form, flat_id: v === "__none" ? "" : v })}>
                                <SelectTrigger data-testid="user-flat-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none">Unassigned</SelectItem>
                                    {flats.map((f) => <SelectItem key={f.id} value={f.id}>{f.block}-{f.number}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>{editing ? "New password (leave blank to keep)" : "Password"}</Label>
                            {!editing && form.role === "resident" ? (
                                <div className="text-xs text-brand-inkSoft bg-brand-sage/40 border border-brand-line rounded-sm p-2.5" data-testid="user-resident-default-password-note">
                                    Residents start on the default password <span className="font-mono font-medium text-brand-ink">Welcome@123</span> and are required to set their own on first login.
                                </div>
                            ) : (
                                <Input data-testid="user-password-input" type="password" required={!editing} minLength={editing ? 0 : 6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm border-brand-line" />
                            )}
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" data-testid="user-submit-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">{editing ? "Save" : "Add"}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <ResidentsMasterImport
                open={masterImportOpen}
                onOpenChange={setMasterImportOpen}
                onDone={load}
            />
        </div>
    );
}
